import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "react-query";
import MaterialReactTable from "material-react-table";
import {
  Spinner,
  Toast,
  ToastBody,
  ToastHeader,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  Button,
} from "reactstrap";
import useAxios from "../../sections/useAxios";
import { toast } from "react-toastify";
import CompanyEditForm from "./components/company-edit-form-modal";
import ConfirmDialog from "../../components/common/confirmation-dialog.jsx";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";

const CompaniesListing = () => {
  const api = useAxios();
  const navigate = useNavigate();
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const {
    data,
    isLoading: companiesLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ["companies-list"],
    async () => {
      const res = await api.get(`company/all-companies`);
      return res.data;
    },
    { cacheTime: 0 }
  );

  const companies = data ?? [];

  const deleteMutation = useMutation(
    async (id) => {
      return api.delete(`company/update-company/${id}`);
    },
    {
      onSuccess: () => {
        toast.success("Company deleted successfully");
        setConfirmDialogOpen(false);
        refetch();
      },
      onError: () => {
        toast.error("Error deleting company");
        setConfirmDialogOpen(false);
      },
    }
  );

  useEffect(() => {
    if (isError) {
      setToastMsg(error.message || "Something went wrong");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    }
  }, [isError, error]);

  const columns = [
    { accessorKey: "id", header: "ID", size: 50 },
    {
      accessorKey: "name",
      header: "Company Name",
      Cell: ({ row }) => (
        <span
          role="button"
          className="text-primary"
          style={{ cursor: "pointer" }}
          onClick={() => navigate(`/company-meetings/${row.original.id}`)}
        >
          <u>{row.original.name}</u>
        </span>
      ),
    },
    { accessorKey: "email", header: "Email" },
    { accessorKey: "location", header: "Location" },
    {
      accessorKey: "meeting_count",
      header: "Meetings",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "participants",
      header: "Investors Per Meeting",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "investor_count",
      header: "Registered Investors",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      Cell: ({ cell }) => new Date(cell.getValue()).toLocaleDateString(),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => (
        <div className="d-flex gap-3 justify-content-center">
          <i
            className="fa-solid fa-pen text-success"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setSelectedCompany(row.original);
              setEditModalOpen(true);
            }}
          ></i>
          <i
            className="fa-solid fa-trash text-danger"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setDeleteId(row.original.id);
              setConfirmDialogOpen(true);
            }}
          ></i>
        </div>
      ),
    },
  ];

  // downlaod excel file function
  const downloadReportMutation = useMutation(
    async () => {
      const res = await api.get("company/admin-report");
      return res.data;
    },
    {
      onSuccess: (data) => {
        if (!data || data.length === 0) {
          toast.error("No data available to export");
          return;
        }

        const sheetData = [];

        // Row 1: Main headers
        sheetData.push(["#", "Company", "Meeting", "Location", "Investor", "", ""]);

        // Row 2: Sub-headers under Investor
        sheetData.push(["", "", "", "", "Name", "Email", "Company"]);

        // Loop through companies
        data.forEach((company, companyIndex) => {
          let firstCompanyRow = true; // show company & location once

          company?.meetings.forEach((meeting) => {
            if (meeting?.investors && meeting?.investors.length > 0) {
              meeting?.investors.forEach((inv, idx) => {
                sheetData.push([
                  firstCompanyRow && idx === 0 ? companyIndex + 1 : "", // use array index as ID
                  firstCompanyRow && idx === 0 ? `${company.company_name} (${company.company_email})` : "",
                  idx === 0 ? `${meeting.meeting_name} (${meeting.start_time} to ${meeting.end_time})` : "",
                  firstCompanyRow && idx === 0 ? company.location : "", // only once per company
                  inv.name,
                  inv.email,
                  inv.company,
                ]);
              });
            } else {
              // Meeting with no investors
              sheetData.push([
                firstCompanyRow ? companyIndex + 1 : "",
                firstCompanyRow ? `${company.company_name} (${company.company_email})` : "",
                `${meeting.meeting_name} (${meeting.start_time} to ${meeting.end_time})`,
                firstCompanyRow ? company.location : "",
                "-",
                "-",
                "-",
              ]);
            }

            firstCompanyRow = false; // company & location already printed
          });
        });

        // Create worksheet
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

        // Merge the "Investor" header across 3 columns (E–G)
        worksheet["!merges"] = [
          { s: { r: 0, c: 4 }, e: { r: 0, c: 6 } },
        ];

        const investorHeaderCell = worksheet["E1"]; // column E, row 1
        if (investorHeaderCell) {
          investorHeaderCell.s = {
            alignment: { horizontal: "center", vertical: "center" },
            font: { bold: true },
          };
        }

        // Generate workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

        // Export file
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, `companies-report-${Date.now()}.xlsx`);

        toast.success("Report downloaded successfully");
      },
      onError: () => {
        toast.error("Failed to download report");
      },
    }
  );

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="primary-color fw-bold">Company's Listing</h4>

        <Button
          className="primary-bg text-white primary-hover"
          onClick={() => downloadReportMutation.mutate()}
          disabled={downloadReportMutation.isLoading}
        >
          {downloadReportMutation.isLoading ? (
            <>
              <Spinner color="light" size="sm" className="me-2" />
              Download Report
            </>
          ) : (
            "Download Report"
          )}
        </Button>
      </div>

      {companiesLoading && (
        <div className="d-flex justify-content-center align-items-center my-4">
          <Spinner color="primary" />
        </div>
      )}

      {showToast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 1050,
            minWidth: "250px",
          }}
        >
          <Toast isOpen={showToast}>
            <ToastHeader icon="danger">Error</ToastHeader>
            <ToastBody>{toastMsg}</ToastBody>
          </Toast>
        </div>
      )}

      {!companiesLoading && !isError && (
        <MaterialReactTable
          columns={columns}
          data={companies}
          muiTablePaperProps={{
            elevation: 10,
          }}
        />
      )}

      {/* ✅ Edit Form Modal */}
      <CompanyEditForm
        isOpen={editModalOpen}
        toggle={() => setEditModalOpen(!editModalOpen)}
        company={selectedCompany}
        refetch={refetch}
      />

      {/* ✅ Delete Confirmation */}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        toggle={() => setConfirmDialogOpen(!confirmDialogOpen)}
        title="Confirm Delete"
        message="Are you sure you want to delete this company?"
        onConfirm={() => deleteMutation.mutate(deleteId)}
        loading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default CompaniesListing;
